/**
 * @format
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import * as authActions from '../../actions/authActions';
import qs from 'qs';
import './css/login.scss';
import LoginForm from "./comps/loginForm"
import UserViewContainer from "./comps/userViewContainer"
import ProductAlert from "./comps/productAlert"
import { useAppSelector } from 'store';
import LoginLogo from './comps/loginLogo';
import LoginFooter from './comps/loginFooter';
import ContactUs from './comps/contactUs';
import MfaMethodForm from './comps/mfaMethodForm';
import MfaCodeForm from './comps/mfaCodeForm';

// Query-string names that would remap an object's prototype instead of setting a normal
// property, so they are rejected before ever being used as an assignment target.
const UNSAFE_QUERY_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const LoginView = () => {

	const dispatch = useDispatch();

	const externalAuth: any = useAppSelector((state: any) => state.config.externalAuth);
	const instance: any = useAppSelector((state: any) => state.config.instance);
	const instanceId: any = useAppSelector((state: any) => state.auth.productInstanceId);
	const isAuthenticated: any = useAppSelector((state: any) => state.auth.isAuthenticated);

	const location = useLocation();
	const [error, setError] = useState('');
	const [loggingIn, setLoggingIn] = useState(false);
	const [redirectToReferrer, setRedirectToReferrer] = useState(false);
	const [showLoadingIndicator, setShowLoadingIndicator] = useState(true);
	const from = location.state?.from || '/';

	const [showMfaMethodForm, setShowMfaMethodForm] = useState(false);
	const [showMfaCodeForm, setShowMfaCodeForm] = useState(false);
	const [mfaEmail, setMfaEmail] = useState('');
	const [mfaMethod, setMfaMethod] = useState('');

	const handleSso = useCallback(() => {
		const returnUrl = typeof from === 'string' ? from : `${from.pathname || '/'}${from.search || ''}`;
		const url = externalAuth.singleSignOnUrl || '/';
		window.location.href = `${url}?returnUrl=${encodeURIComponent(returnUrl)}`;
	}, [from, externalAuth.singleSignOnUrl]);

	useEffect(() => {
		if (!isAuthenticated && externalAuth.isAutoLoginEnabled) {
			handleSso();
		} else if (instance.isLoaded) {
			setShowLoadingIndicator(false);
		}
	}, [isAuthenticated, externalAuth, instance, handleSso]);

	useEffect(() => {
		const queryString = location?.search || '';
		const rawParams = qs.parse(queryString, { ignoreQueryPrefix: true });
		const optionalParams: { [key: string]: any } = Object.create(null);
		Object.keys(rawParams).forEach((key) => {
			const name = key.toLowerCase();
			if (UNSAFE_QUERY_KEYS.has(name)) return;
			optionalParams[name] = rawParams[key];
		});

		dispatch(authActions.setGRDebugContext({ debug: optionalParams.debug === 'on' }))

	}, [dispatch, location]);

	const handleAuthenticateUser = async (email: any, password: any) => {
		setLoggingIn(true);
		try {
			const response: any = await dispatch(authActions.authenticateUser(email, password, instanceId, '', ''))
			if (response.error) {
				if (response.error.response.data && typeof response.error.response.data === 'string') {
					setError(response.error.response.data);
				} else {
					const request = response.error.request;
					if (request.status !== 500) {
						const errorMessage = request.responseText.replace(/['"]+/g, '');
						setError(`An error occurred while trying to log in. ${errorMessage}`);
					}
					else {
						setError('An error occurred while trying to log in. Please refresh and try again.')
					}
				}
			} else if (response.payload.data.requiresMfa) {
					setMfaEmail(email);
					setShowMfaMethodForm(true);
			} else {
					setRedirectToReferrer(true);
			}
		} catch {
			setError('An error occurred while trying to log in.');
		} finally {
			setShowLoadingIndicator(false);
			setLoggingIn(false);
		}
	};

	const handleMfaMethodSubmit = async (selectedMethod: string) => {
		setMfaMethod(selectedMethod);
		setError('');

		await dispatch(authActions.sendMfaCode(mfaEmail, selectedMethod));
		setShowMfaMethodForm(false);
		setShowMfaCodeForm(true);
	};

	const handleMfaCodeSubmit = async (code: string) => {
		setLoggingIn(true);
		setError('');

		try {
			const response: any = await dispatch(authActions.authenticateUser(mfaEmail, '', instanceId, mfaMethod, code))
			if (response.error) {
				setError('An error occurred while verifying the code.');
			} else {
				setRedirectToReferrer(true);
			}
		} catch (error: any) {
			setError('An error occurred while verifying the code.');
		} finally {
			setLoggingIn(false);
			setShowLoadingIndicator(false);
		}
	};

	const handleMfaCodeResend = async () => {
		setError('');
		await dispatch(authActions.sendMfaCode(mfaEmail, mfaMethod));
	}

	const handleMfaCodeCancel = async () => {
		setError('');
		setShowMfaMethodForm(true);
		setShowMfaCodeForm(false);
	}

	const handleCancel = async () => {
		setShowMfaMethodForm(false);
	}

	if (redirectToReferrer && !loggingIn) {
		return <Navigate to={from} />;
	}

	return (
		<UserViewContainer isLoading={showLoadingIndicator} className='loginView'>
			<div className="loginWindow">
				<ProductAlert />
				<LoginLogo />
				{!showMfaMethodForm && !showMfaCodeForm && (
					<>
						<LoginForm
							handleSubmit={handleAuthenticateUser}
							handleSsoLogin={handleSso}
							isLoading={loggingIn}
							error={error}
						/>
						<LoginFooter />
						<ContactUs />
					</>
				)}
				{showMfaMethodForm && (
					<MfaMethodForm
						onSubmit={handleMfaMethodSubmit}
						onCancel={handleCancel}
						error={error}
						isLoading={loggingIn}
					/>
				)}
				{showMfaCodeForm && (
					<MfaCodeForm
						isLoading={loggingIn}
						error={error}
						onSubmit={handleMfaCodeSubmit}
						onCancel={handleMfaCodeCancel}
						onResendCode={handleMfaCodeResend}
					/>
				)}
			</div>
		</UserViewContainer>
	)
}

export default LoginView;